// Ref: https://github.com/chakra-ui/chakra-ui/blob/main/packages/theme/src/components/button.ts

import { transparentize } from '@/util/chakra';
import type { SystemStyleObject } from '@chakra-ui/styled-system';
import { defineStyle, defineStyleConfig } from '@chakra-ui/styled-system';
import { mode } from '@chakra-ui/theme-tools';
import styleguide from '@common/styleguide';

const baseStyle = defineStyle({
    borderRadius: '8px',
    fontWeight: 'medium',
});

const sizes = {
    lg: defineStyle({
        h: 14,
        minW: 14,
        fontSize: 'lg',
        fontWeight: 'semibold',
        px: 9,
    }),
    md: defineStyle({
        h: 14,
        minW: 12,
        fontSize: 'md',
        fontWeight: 'semibold',
        px: 9,
    }),
    sm: defineStyle({
        h: 9,
        minW: 9,
        fontSize: 'sm',
        fontWeight: 'medium',
        px: 5,
    }),
    xs: defineStyle({
        h: 6,
        minW: 6,
        fontSize: 'xs',
        fontWeight: 'medium',
        px: 3,
    }),
};

const variantGhost = defineStyle((props) => {
    const { colorScheme: c, theme } = props;

    const color = `${c}.500`;
    const darkHoverBg = transparentize(color, 0.12)(theme);
    const darkActiveBg = transparentize(color, 0.24)(theme);

    if (c === 'gray') {
        return {
            color: mode('gray.500', `whiteAlpha.900`)(props),
            _hover: {
                bg: mode('gray.100', `whiteAlpha.200`)(props),
            },
            _active: { bg: mode('gray.100', `whiteAlpha.300`)(props) },
        };
    }

    if (c === 'lightGray') {
        return {
            color: mode('gray.400', `whiteAlpha.800`)(props),
            _hover: {
                color: mode('gray.500', `whiteAlpha.900`)(props),
                bg: mode('gray.100', `whiteAlpha.200`)(props),
            },
            _active: {
                color: mode('gray.500', `whiteAlpha.900`)(props),
                bg: mode('gray.100', `whiteAlpha.300`)(props),
            },
        };
    }

    return {
        color,
        bg: 'transparent',
        _hover: {
            bg: darkHoverBg,
        },
        _active: {
            bg: darkActiveBg,
        },
    };
});

const variantInset = defineStyle((props) => {
    return {
        color: mode('gray.700', 'whiteAlpha.800')(props),
        bg: mode('gray.100', 'blackAlpha.100')(props),
        _hover: {
            color: mode('textMuted', 'white')(props),
            bg: mode('gray.50', 'blackAlpha.50')(props),
        },
        _active: {
            color: mode('textMuted', 'white')(props),
            bg: mode('gray.50', 'gray.700')(props),
        },
        // From Chakra - kludge to play nice with ButtonGroup component
        '.chakra-button__group[data-attached] > &:not(:last-of-type)': {
            marginEnd: '-1px',
        },
    };
});

const variantOutline = defineStyle((props) => {
    const { colorScheme: c } = props;

    if (c === 'gray' || c === 'white' || c === 'dark') {
        const hoverState = {
            color: 'text',
            bg: 'white',
            borderColor: 'text',
        };
        return {
            color: 'text',
            border: '1px solid',
            borderColor: c === 'dark' ? 'text' : 'gray.300',
            bg: 'white',
            _hover: hoverState,
            _active: hoverState,
            _focus: hoverState,
            // From Chakra - kludge to play nice with ButtonGroup component
            '.chakra-button__group[data-attached] > &:not(:last-of-type)': {
                marginEnd: '-1px',
            },
        };
    }

    return {
        color: `${c}.500`,
        border: '1px solid',
        borderColor: `${c}.500`,
        bg: 'transparent',
        _hover: {
            bg: `${c}.50`,
        },
        _active: {
            bg: `${c}.50`,
        },
        _focus: {
            bg: `${c}.50`,
        },
        // From Chakra - kludge to play nice with ButtonGroup component
        '.chakra-button__group[data-attached] > &:not(:last-of-type)': {
            marginEnd: '-1px',
        },
    };
});

const variantSolid = defineStyle((props) => {
    const { colorScheme: c } = props;

    let bg = `${c}.500`;
    let hoverBg = `${c}.400`;
    let activeBg = `${c}.600`;

    if (c === 'dark') {
        bg = 'blackAlpha.800';
        hoverBg = 'blackAlpha.700';
        activeBg = 'blackAlpha.900';
    }

    return {
        color: 'white',
        bg,
        _hover: {
            bg: hoverBg,
        },
        _active: {
            bg: activeBg,
        },
        // From Chakra - kludge to play nice with ButtonGroup component
        '.chakra-button__group[data-attached] > &:not(:last-of-type)': {
            marginEnd: '-1px',
        },
    };
});

const variantMuted: SystemStyleObject = {
    bg: 'none',
    color: 'gray.600',
    display: 'inline',
    lineHeight: 'inherit',
    m: 0,
    p: 0,
    _hover: {
        color: 'textMuted',
    },
};

const variantPillDisabledStyles = defineStyle((props) => {
    const { colorScheme: c } = props;
    return {
        bg: mode(`${c}.200`, `${c}.500`)(props),
        borderColor: mode(`${c}.200`, `${c}.500`)(props),
        opacity: 0.25,
    };
});

const variantPillBaseStyles = {
    rounded: 'full',
    border: '1px solid',
};

const variantPill = defineStyle((props) => {
    const { colorScheme: c } = props;

    if (c === 'white') {
        return {
            ...variantPillBaseStyles,
            bg: 'white',
            borderColor: 'border',
            _hover: {
                borderColor: styleguide.colors.grayLighter,
                _disabled: {
                    ...variantPillDisabledStyles(props),
                },
            },
            _disabled: {
                ...variantPillDisabledStyles(props),
            },
        };
    }

    if (c === 'dark') {
        return {
            ...variantPillBaseStyles,
            bg: 'blackAlpha.800',
            color: 'white',
            _hover: {
                shadow: 'md',
                bg: 'blackAlpha.700',
                _disabled: {
                    ...variantPillDisabledStyles(props),
                },
            },
            _disabled: {
                ...variantPillDisabledStyles(props),
            },
        };
    }

    if (c === 'gray') {
        return {
            ...variantPillBaseStyles,
            bg: 'gray.50',
            borderColor: 'gray.50',
            _hover: {
                bg: 'gray.100',
                _disabled: {
                    ...variantPillDisabledStyles(props),
                },
            },
            _disabled: {
                ...variantPillDisabledStyles(props),
            },
        };
    }

    return {
        ...variantPillBaseStyles,
        bg: mode(`${c}.200`, `${c}.500`)(props),
        borderColor: `${c}.200`,
        _hover: {
            bg: mode('white', 'whiteAlpha.500')(props),
            borderColor: `${c}.400`,
            _disabled: {
                ...variantPillDisabledStyles(props),
            },
        },
        _disabled: {
            ...variantPillDisabledStyles(props),
        },
    };
});

const variantLink = defineStyle((props) => {
    const { colorScheme: c } = props;
    return {
        color: mode(`${c}.500`, `${c}.200`)(props),
        padding: 0,
        height: 'auto',
        lineHeight: 'normal',
        verticalAlign: 'baseline',
        textDecoration: 'none',
        _hover: {
            color: mode(`${c}.400`, `${c}.100`)(props),
            textDecoration: 'none',
        },
        _active: {
            color: mode(`${c}.400`, `${c}.100`)(props),
            textDecoration: 'none',
        },
    };
});

const variants = {
    ghost: variantGhost,
    outline: variantOutline,
    inset: variantInset,
    solid: variantSolid,
    muted: variantMuted,
    pill: variantPill,
    link: variantLink,
};

const buttonTheme = defineStyleConfig({
    baseStyle,
    sizes,
    variants,
    defaultProps: {
        variant: 'solid',
        size: 'md',
        colorScheme: 'blue',
    },
});

export default buttonTheme;
